export const restaurantHero = {
  eyebrow: '味聚智慧餐饮',
  titleLine1: '让每一餐',
  titleLine2: '从经营到体验都',
  titleAccent: '更聪明',
  subtitle:
    '味聚以餐厅经营为核心，连接前厅、后厨、供应链与消费者，把订单、库存、服务与数据统一到一套可协同的平台。',
  note: '从门店单体数字化，走向供需协同与用户增长。',
  stats: [
    { value: '9', unit: '大', label: '经营环节一体打通' },
    { value: '3', unit: '端', label: '门店 · 供应链 · 用户协同' },
    { value: '1', unit: '套', label: '统一经营数据底座' },
  ],
  tags: ['前厅经营', '后厨协同', '供应链', '用户运营', '平台治理'],
};

export const painPoints = [
  {
    id: 'order',
    icon: 'ri-shuffle-line',
    title: '订单分散',
    desc: '前台与后厨信息不同步，多系统切换导致漏单、错单与对账困难。',
  },
  {
    id: 'stock',
    icon: 'ri-stack-line',
    title: '库存不准',
    desc: '采购与出品难以协同，缺货、积压与损耗无法及时掌握。',
  },
  {
    id: 'supplier',
    icon: 'ri-team-line',
    title: '供应商难管',
    desc: '供应商多，资质和履约质量参差，缺少统一准入与结算。',
  },
  {
    id: 'member',
    icon: 'ri-vip-crown-line',
    title: '会员难复购',
    desc: '会员和活动有数据，却难转化为稳定的复购与品牌忠诚。',
  },
  {
    id: 'governance',
    icon: 'ri-shield-keyhole-line',
    title: '治理更复杂',
    desc: '门店变多后，权限、结算和审计更复杂，风险难以追溯。',
  },
];

export const businessFlow = [
  { id: 'org', icon: 'ri-building-4-line', title: '组织与门店配置', desc: '按组织、门店与角色建立统一权限与经营档案' },
  { id: 'menu', icon: 'ri-restaurant-2-line', title: '菜品 / 套餐管理', desc: 'SPU / SKU 与套餐 BOM 统一维护' },
  { id: 'pos', icon: 'ri-cash-line', title: '订单收银', desc: '点单、收银、优惠与结账一体化处理' },
  { id: 'verify', icon: 'ri-qr-scan-line', title: '到店核销', desc: '团购券、权益券到店扫码核销' },
  { id: 'kds', icon: 'ri-restaurant-line', title: '后厨出品', desc: '订单进入 KDS，制作、出品、完成实时衔接' },
  { id: 'stock', icon: 'ri-archive-line', title: '库存采购', desc: '库存预警、采购、验收与调拨闭环' },
  { id: 'member', icon: 'ri-heart-3-line', title: '客户运营', desc: '身份成长、权益券与消息触达' },
  { id: 'settle', icon: 'ri-bank-card-line', title: '财务结算', desc: '营收、结算与对账口径统一留痕' },
  { id: 'data', icon: 'ri-line-chart-line', title: '数据复盘', desc: '经营指标汇总与异常定位复盘' },
];

export const capabilities = [
  {
    id: 'hub',
    icon: 'ri-store-2-line',
    title: '门店经营中枢',
    desc: '收银、订单、核销、桌台与开店 / 营业 / 收店集中管理，让门店日常运营有统一入口。',
    keywords: ['收银', '订单', '核销', '桌台'],
    highlight: false,
  },
  {
    id: 'kds',
    icon: 'ri-restaurant-line',
    title: '前后场协同',
    desc: '订单进入 KDS，制作、出品、完成状态实时衔接，减少前台与后厨的信息断层。',
    keywords: ['KDS', '分单', '出品状态', '备餐'],
    highlight: false,
  },
  {
    id: 'supply',
    icon: 'ri-truck-line',
    title: '供应链与质量追溯',
    desc: '采购、仓储、配送、验收、批次、温控和质量记录可追溯，让每一份食材来源清晰。',
    keywords: ['批次', '温控', '抽检', '召回'],
    highlight: true,
  },
  {
    id: 'member',
    icon: 'ri-vip-crown-line',
    title: '客户与权益运营',
    desc: '聚餐、AI 推荐、优惠券、身份成长和消息触达，把一次消费转化为长期关系。',
    keywords: ['聚餐', 'AI 推荐', '优惠券', '消息触达'],
    highlight: false,
  },
  {
    id: 'governance',
    icon: 'ri-shield-check-line',
    title: '平台数据与治理',
    desc: '数据驾驶舱、角色权限、审批审计、备份和灰度发布，为规模化经营提供可信底座。',
    keywords: ['驾驶舱', '权限', '审计', '灰度'],
    highlight: false,
  },
];

export const scenarios = [
  {
    id: 'chain',
    icon: 'ri-store-3-line',
    title: '连锁餐饮',
    desc: '多门店统一商品与营销策略，分级权限管理，集中查看营收、订单、库存和异常。',
    image:
      'https://readdy.ai/api/search-image?query=Warm%20modern%20chain%20restaurant%20interior%20with%20multiple%20cozy%20dining%20tables%20and%20soft%20warm%20ambient%20lighting%2C%20clean%20minimal%20aesthetic%2C%20inviting%20atmosphere%2C%20editorial%20photography%2C%20high%20detail%2C%20harmonious%20warm%20amber%20tones%20composition&width=640&height=420&seq=restaurant-scene-chain-01&orientation=landscape',
  },
  {
    id: 'supply',
    icon: 'ri-truck-line',
    title: '餐饮供应链',
    desc: '供应商准入、订单履约、仓配、批次追溯和结算闭环。',
    image:
      'https://readdy.ai/api/search-image?query=Clean%20modern%20food%20supply%20chain%20warehouse%20with%20organized%20fresh%20produce%20crates%20and%20logistics%20shelving%2C%20fresh%20green%20vegetables%20and%20packaged%20ingredients%2C%20soft%20natural%20lighting%2C%20editorial%20photography%2C%20high%20detail%2C%20warm%20neutral%20tones&width=640&height=420&seq=restaurant-scene-supply-02&orientation=landscape',
  },
  {
    id: 'party',
    icon: 'ri-group-line',
    title: '聚餐与团购',
    desc: 'AI 定制或自主选餐，承接邀请、报名、支付、核销和评价。',
    image:
      'https://readdy.ai/api/search-image?query=Warm%20festive%20gathering%20around%20a%20dining%20table%20with%20friends%20sharing%20dishes%20and%20toasting%2C%20cozy%20restaurant%20atmosphere%20with%20soft%20warm%20lighting%2C%20joyful%20social%20dining%20experience%2C%20editorial%20photography%2C%20high%20detail%2C%20warm%20amber%20tones&width=640&height=420&seq=restaurant-scene-party-03&orientation=landscape',
  },
  {
    id: 'mall',
    icon: 'ri-shopping-bag-3-line',
    title: '餐饮采购商城',
    desc: '精选食材、工厂直供和进口食品，支持搜索、筛选、加购、下单、配送和售后。',
    image:
      'https://readdy.ai/api/search-image?query=Clean%20modern%20online%20grocery%20shopping%20interface%20with%20fresh%20produce%20and%20packaged%20ingredients%20displayed%20in%20a%20grid%2C%20premium%20food%20e-commerce%20aesthetic%2C%20soft%20studio%20lighting%2C%20editorial%20product%20photography%2C%20high%20detail%2C%20warm%20neutral%20tones&width=640&height=420&seq=restaurant-scene-mall-04&orientation=landscape',
  },
];

export const dataMetrics = [
  { id: 'revenue', label: '营收', value: '—', unit: '元', icon: 'ri-funds-line', status: '示意' },
  { id: 'order', label: '订单量', value: '—', unit: '单', icon: 'ri-file-list-3-line', status: '示意' },
  { id: 'stock', label: '库存周转', value: '—', unit: '天', icon: 'ri-archive-line', status: '示意' },
  { id: 'fulfill', label: '履约率', value: '—', unit: '%', icon: 'ri-check-double-line', status: '示意' },
  { id: 'customer', label: '复购率', value: '—', unit: '%', icon: 'ri-heart-3-line', status: '示意' },
  { id: 'settle', label: '结算金额', value: '—', unit: '元', icon: 'ri-bank-card-line', status: '示意' },
];

export const securityFeatures = [
  { id: 'least', icon: 'ri-lock-line', title: '最小权限', desc: '按组织与岗位分配最小权限，职责分离' },
  { id: 'approval', icon: 'ri-checkbox-circle-line', title: '授权审批', desc: '关键操作经过授权、审批与留痕' },
  { id: 'audit', icon: 'ri-file-search-line', title: '操作审计', desc: '操作记录全程可审计、可追溯' },
  { id: 'backup', icon: 'ri-database-2-line', title: '数据备份', desc: '数据定期备份，异常可回滚' },
  { id: 'mfa', icon: 'ri-smartphone-line', title: 'MFA 认证', desc: '多因素认证，降低账号风险' },
  { id: 'session', icon: 'ri-router-line', title: 'IP 与会话管理', desc: '登录会话与访问来源统一管理' },
  { id: 'gray', icon: 'ri-toggle-line', title: '功能灰度', desc: '新功能灰度发布，降低上线风险' },
  { id: 'rollback', icon: 'ri-arrow-go-back-line', title: '异常回滚', desc: '故障快速定位与版本回滚' },
];

export const roadmap = [
  {
    id: 'diagnose',
    icon: 'ri-search-eye-line',
    phase: '诊断',
    title: '梳理业务现状',
    desc: '梳理门店、组织、商品、供应商、订单和结算，明确业务链路与痛点。',
  },
  {
    id: 'launch',
    icon: 'ri-rocket-line',
    phase: '上线',
    title: '打通核心链路',
    desc: '优先打通商品、订单、收银、KDS、库存，让门店经营先跑起来。',
  },
  {
    id: 'synergy',
    icon: 'ri-link',
    phase: '协同',
    title: '接入供应链与治理',
    desc: '逐步接入供应链、权益和数据治理，形成完整协同能力。',
  },
  {
    id: 'grow',
    icon: 'ri-bar-chart-line',
    phase: '增长',
    title: '数据驱动运营',
    desc: '基于数据复盘，优化商品结构、会员运营与经营策略。',
  },
  {
    id: 'replicate',
    icon: 'ri-file-copy-line',
    phase: '复制',
    title: '沉淀标准复制',
    desc: '沉淀标准化能力，向更多门店与区域复制推广。',
  },
];

export const consultation = {
  eyebrow: '预约咨询',
  title: '从一间门店开始，建立可复制的智慧餐饮能力',
  desc: '告诉我们您的门店规模、经营模式和当前系统，我们将基于业务链路给出适配建议，从诊断、上线到协同、增长、复制，陪伴您的餐饮数字化之路。',
  primaryLabel: '预约方案沟通',
  secondaryLabel: '查看解决方案架构',
  formTitle: '预约智慧餐饮顾问',
  success: '您的需求已提交，我们将尽快与您联系，为您提供适配的智慧餐饮建设建议。',
};